import { useCallback, useEffect, useRef, useState } from "react";
import { fill, useTexts } from "../texts.js";
import { acceptAttribute, AttachmentError, checkAttachment, attachmentOptions, prepareImage } from "./attachments.js";
const toUi = (m) => ({
    id: m.id,
    role: m.role,
    content: m.content,
    ...(m.attachments?.length ? { attachments: m.attachments } : {}),
    ...(m.tools?.length ? { tools: m.tools } : {}),
    ...(m.proposals?.length ? { proposals: m.proposals } : {}),
});
const byUpdated = (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
let seq = 0;
const localId = () => `local-${Date.now()}-${++seq}`;
const urlKey = (sessionId, attachmentId) => `${sessionId}\n${attachmentId}`;
const revoke = (p) => void p.then((u) => URL.revokeObjectURL(u), () => undefined);
/**
 * Conversations and answer streaming. Messages are cached per session in
 * memory (the cache keeps the proposal buttons of streamed answers). The
 * backend object may change between renders; the latest is used.
 */
export function useAssistant(backend, opts = {}) {
    const { contextId: ctxId, contextTitle, initialSession } = opts;
    const follow = opts.follow ?? true;
    const t = useTexts();
    const be = useRef(backend);
    be.current = backend;
    const cb = useRef(opts);
    cb.current = opts;
    const attachmentsEnabled = !!backend.uploadAttachment && opts.attachments !== false;
    const attOpts = opts.attachments || undefined;
    const [sessions, setSessions] = useState([]);
    const [sessionsLoaded, setSessionsLoaded] = useState(false);
    const [sessionsError, setSessionsError] = useState(null);
    const [sessionId, setSessionIdState] = useState(null);
    const sidRef = useRef(null);
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState(null);
    const [stream, setStream] = useState(null);
    const cache = useRef(new Map());
    const abortRef = useRef(null);
    const pickedFor = useRef(null);
    // images of the next message: state for rendering, a ref for send (which
    // may run right after `await addFiles(...)`, before a re-render)
    const [pending, setPendingState] = useState([]);
    const pendingRef = useRef([]);
    const blobs = useRef(new Map());
    // pending id -> the stored attachment of a partly failed send (not uploaded twice)
    const uploaded = useRef(new Map());
    // (session, attachment) -> object URL; uploaded previews move in here
    const urls = useRef(new Map());
    const setPending = useCallback((fn) => {
        pendingRef.current = fn(pendingRef.current);
        setPendingState(pendingRef.current);
    }, []);
    const patchPending = useCallback((id, patch) => setPending((l) => l.map((p) => (p.id === id ? { ...p, ...patch } : p))), [setPending]);
    const clearPending = useCallback(() => {
        for (const p of pendingRef.current)
            if (p.previewUrl)
                URL.revokeObjectURL(p.previewUrl);
        blobs.current.clear();
        uploaded.current.clear();
        setPending(() => []);
    }, [setPending]);
    const setSessionId = useCallback((id) => {
        sidRef.current = id;
        setSessionIdState(id);
    }, []);
    const reloadSessions = useCallback(async () => {
        try {
            const list = await be.current.listSessions();
            setSessions(list);
            setSessionsError(null);
        }
        catch (e) {
            setSessionsError(e);
        }
        finally {
            setSessionsLoaded(true);
        }
    }, []);
    const changed = useCallback(() => {
        void reloadSessions();
        cb.current.onSessionsChanged?.();
    }, [reloadSessions]);
    useEffect(() => {
        void reloadSessions();
    }, [reloadSessions]);
    // follow the context (not while an answer is streaming)
    useEffect(() => {
        if (!follow || !sessionsLoaded || stream)
            return;
        const key = ctxId ?? "";
        if (pickedFor.current === key)
            return;
        pickedFor.current = key;
        const match = sessions.filter((s) => (s.contextId ?? "") === key).sort(byUpdated)[0];
        setSessionId(match?.id ?? null);
    }, [follow, ctxId, sessions, sessionsLoaded, stream, setSessionId]);
    // page mode: open the handed-over conversation once
    const initDone = useRef(false);
    useEffect(() => {
        if (follow || initDone.current || !sessionsLoaded)
            return;
        initDone.current = true;
        if (initialSession && sessions.some((s) => s.id === initialSession))
            setSessionId(initialSession);
    }, [follow, initialSession, sessions, sessionsLoaded, setSessionId]);
    const current = sessions.find((s) => s.id === sessionId);
    // a conversation about a context keeps it, wherever it is continued
    const contextId = sessionId && current ? current.contextId || (follow ? ctxId : undefined) : ctxId;
    // image URLs of other conversations are released when the conversation changes
    useEffect(() => {
        const keep = `${sessionId ?? ""}\n`;
        for (const [k, p] of urls.current) {
            if (!k.startsWith(keep)) {
                revoke(p);
                urls.current.delete(k);
            }
        }
    }, [sessionId]);
    // and every URL on unmount
    useEffect(() => {
        const u = urls.current;
        const b = blobs.current;
        return () => {
            for (const p of u.values())
                revoke(p);
            u.clear();
            for (const p of pendingRef.current)
                if (p.previewUrl)
                    URL.revokeObjectURL(p.previewUrl);
            b.clear();
        };
    }, []);
    // messages of the selected conversation
    useEffect(() => {
        setLoadError(null);
        if (!sessionId) {
            setMessages([]);
            return;
        }
        const cached = cache.current.get(sessionId);
        if (cached) {
            setMessages(cached);
            return;
        }
        let alive = true;
        setLoading(true);
        setMessages([]);
        be.current
            .messages(sessionId)
            .then((ms) => {
            const list = ms.map((m) => toUi(m));
            cache.current.set(sessionId, list);
            if (alive && sidRef.current === sessionId)
                setMessages(list);
        })
            .catch((e) => alive && setLoadError(e))
            .finally(() => alive && setLoading(false));
        return () => {
            alive = false;
        };
    }, [sessionId]);
    const update = useCallback((sid, fn) => {
        const next = fn(cache.current.get(sid) ?? []);
        cache.current.set(sid, next);
        if (sidRef.current === sid)
            setMessages(next);
    }, []);
    const addFiles = useCallback(async (files) => {
        if (!attachmentsEnabled)
            return;
        const jobs = [];
        for (const file of Array.from(files)) {
            const id = localId();
            const count = pendingRef.current.filter((p) => p.status !== "error").length;
            const refused = checkAttachment(file, attOpts, count);
            const base = {
                id,
                name: file.name || t.assistantImage,
                mime: file.type,
                size: file.size,
                previewUrl: "",
                status: "preparing",
            };
            if (refused) {
                const error = fill(t[refused], { n: attachmentOptions(attOpts).maxCount });
                setPending((l) => [...l, { ...base, status: "error", error }]);
                continue;
            }
            setPending((l) => [...l, base]);
            jobs.push(prepareImage(file, attOpts).then((img) => {
                if (!pendingRef.current.some((p) => p.id === id))
                    return; // removed meanwhile
                blobs.current.set(id, img.blob);
                patchPending(id, { status: "ready", mime: img.mime, size: img.blob.size, previewUrl: URL.createObjectURL(img.blob) });
            }, (e) => {
                const key = e instanceof AttachmentError ? e.key : "assistantAttachmentType";
                patchPending(id, { status: "error", error: t[key] });
            }));
        }
        await Promise.all(jobs);
    }, [attachmentsEnabled, attOpts, setPending, patchPending, t]);
    const removePending = useCallback((id) => {
        const p = pendingRef.current.find((x) => x.id === id);
        if (!p || p.status === "uploading")
            return;
        if (p.previewUrl)
            URL.revokeObjectURL(p.previewUrl);
        blobs.current.delete(id);
        uploaded.current.delete(id);
        setPending((l) => l.filter((x) => x.id !== id));
    }, [setPending]);
    const attachmentUrl = useCallback((a) => {
        const sid = sidRef.current;
        if (!sid)
            return undefined;
        const key = urlKey(sid, a.id);
        const hit = urls.current.get(key);
        if (hit)
            return hit;
        const b = be.current;
        if (!b.attachmentUrl)
            return undefined;
        const p = b.attachmentUrl(sid, a);
        urls.current.set(key, p);
        p.catch(() => {
            if (urls.current.get(key) === p)
                urls.current.delete(key);
        });
        return p;
    }, []);
    const send = useCallback(async (text) => {
        if (abortRef.current)
            return false;
        const ready = attachmentsEnabled ? pendingRef.current.filter((p) => p.status === "ready") : [];
        if (!text && ready.length === 0 && attachmentsEnabled)
            return false;
        cb.current.onSend?.(text);
        let sid = sidRef.current;
        const ac = new AbortController();
        abortRef.current = ac;
        const st = { text: "", reasoning: "", tools: [], proposals: [] };
        setStream({ ...st });
        let error;
        let started = false;
        try {
            if (!sid) {
                const title = (contextTitle && contextId ? `${contextTitle}: ` : "") + (text ? text.slice(0, 60) : t.assistantImageTitle);
                const s = await be.current.createSession(contextId, title);
                sid = s.id;
                cache.current.set(sid, []);
                pickedFor.current = ctxId ?? "";
                setSessionId(sid);
                changed();
            }
            let sent;
            const upload = be.current.uploadAttachment;
            if (ready.length && upload) {
                sent = [];
                for (const p of ready) {
                    const done = uploaded.current.get(p.id);
                    if (done && done.sessionId === sid) {
                        sent.push(done.attachment);
                        continue;
                    }
                    const blob = blobs.current.get(p.id);
                    if (!blob)
                        continue;
                    patchPending(p.id, { status: "uploading", progress: undefined });
                    try {
                        const a = await upload.call(be.current, sid, blob, p.name, ac.signal, (f) => patchPending(p.id, { progress: Math.max(0, Math.min(1, f)) }));
                        uploaded.current.set(p.id, { sessionId: sid, attachment: a });
                        sent.push(a);
                        patchPending(p.id, { status: "ready" });
                    }
                    catch {
                        patchPending(p.id, { status: "error", error: t.assistantAttachmentFailed });
                        return false; // the finally below ends the attempt; nothing was sent
                    }
                }
                // the previews show the sent images at once; the cache owns them now
                const sentIds = new Set(ready.map((p) => p.id));
                for (const p of ready) {
                    const a = uploaded.current.get(p.id)?.attachment;
                    if (a && p.previewUrl)
                        urls.current.set(urlKey(sid, a.id), Promise.resolve(p.previewUrl));
                    blobs.current.delete(p.id);
                    uploaded.current.delete(p.id);
                }
                setPending((l) => l.filter((x) => !sentIds.has(x.id)));
            }
            started = true;
            update(sid, (l) => [...l, { id: localId(), role: "user", content: text, ...(sent?.length ? { attachments: sent } : {}) }]);
            const events = sent ? be.current.stream(sid, text, contextId, ac.signal, sent) : be.current.stream(sid, text, contextId, ac.signal);
            for await (const ev of events) {
                switch (ev.type) {
                    case "text_delta":
                        st.text += ev.text ?? "";
                        break;
                    case "reasoning_delta":
                        st.reasoning += ev.text ?? "";
                        break;
                    case "tool_call":
                        st.tools = [...st.tools, { name: ev.text ?? "tool", status: "running" }];
                        break;
                    case "tool_result": {
                        const i = st.tools.map((x) => x.status).lastIndexOf("running");
                        if (i >= 0) {
                            st.tools = st.tools.map((x, j) => j === i ? { ...x, status: ev.block_type === "error" ? "error" : "done", result: ev.text } : x);
                        }
                        break;
                    }
                    case "proposal":
                        if (ev.proposal !== undefined)
                            st.proposals = [...st.proposals, ev.proposal];
                        break;
                    case "error":
                        error = ev.error || ev.text || t.assistantStreamError;
                        break;
                }
                setStream({ ...st });
            }
        }
        catch (e) {
            started = true;
            if (ac.signal.aborted)
                error = t.assistantAborted;
            else
                error = e instanceof Error ? e.message : String(e);
        }
        finally {
            abortRef.current = null;
            if (sid && started) {
                update(sid, (l) => [
                    ...l,
                    {
                        id: localId(),
                        role: "assistant",
                        content: st.text,
                        reasoning: st.reasoning || undefined,
                        tools: st.tools.length ? st.tools : undefined,
                        proposals: st.proposals.length ? st.proposals : undefined,
                        error,
                    },
                ]);
            }
            setStream(null);
            changed();
        }
        return true;
    }, [
        attachmentsEnabled,
        ctxId,
        contextId,
        contextTitle,
        setSessionId,
        update,
        changed,
        patchPending,
        setPending,
        t.assistantAborted,
        t.assistantStreamError,
        t.assistantImageTitle,
        t.assistantAttachmentFailed,
    ]);
    const cancel = useCallback(() => abortRef.current?.abort(), []);
    const newChat = useCallback(() => {
        if (abortRef.current)
            return;
        pickedFor.current = ctxId ?? "";
        clearPending();
        setSessionId(null);
    }, [ctxId, setSessionId, clearPending]);
    const selectSession = useCallback((id) => {
        if (abortRef.current)
            return;
        if (id !== sidRef.current)
            clearPending();
        setSessionId(id);
    }, [setSessionId, clearPending]);
    const removeSession = useCallback(async (id) => {
        await be.current.deleteSession(id);
        cache.current.delete(id);
        if (sidRef.current === id)
            setSessionId(null);
        changed();
    }, [setSessionId, changed]);
    const renameSession = useCallback(async (id, title) => {
        await be.current.renameSession(id, title);
        changed();
    }, [changed]);
    return {
        sessions,
        sessionsLoaded,
        sessionsError,
        reloadSessions,
        sessionId,
        current,
        contextId,
        messages,
        loading,
        loadError,
        stream,
        streaming: !!stream,
        send,
        cancel,
        newChat,
        selectSession,
        removeSession,
        renameSession,
        attachmentsEnabled,
        accept: acceptAttribute(attOpts),
        pending,
        addFiles,
        removePending,
        attachmentUrl,
    };
}
